import { useMutation } from '@tanstack/react-query';
import { edupassClient, falconClient } from '@/data/@clients';
import { setAuthToken } from '@/utils/auth-token';
import {
  check_email_session_path,
  forgot_password_path,
  reset_password_path,
} from '@/utils/routes';

interface LoginProps {
  access: string;
  password: string;
}

interface ResetPasswordProps {
  senha: string;
  token: string;
}

interface RegisterPasswordParams {
  password: string;
  token: string;
}

interface CheckEmailResponse {
  exists: boolean;
  isSso?: boolean;
  ssoUrl?: string;
  message?: string;
}

interface PasswordResetMessageResponse {
  message: string;
}

interface ResetPasswordResponse extends PasswordResetMessageResponse {
  token?: string;
}

export const useCheckEmail = () => {
  return useMutation({
    mutationFn: async (login: string): Promise<CheckEmailResponse> =>
      await falconClient.get(check_email_session_path({ login })),
  });
};

export const useLogin = () => {
  return useMutation({
    mutationFn: async ({ access, password }: LoginProps) => {
      const data = { access, password };
      return await edupassClient.post('/auth/login', data);
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (access: string) => {
      return await falconClient.post<
        PasswordResetMessageResponse,
        PasswordResetMessageResponse
      >(forgot_password_path(), { access });
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async ({ senha, token }: ResetPasswordProps) => {
      const data = await falconClient.post<
        ResetPasswordResponse,
        ResetPasswordResponse
      >(reset_password_path(), { password: senha, token });

      // Falcon already sets the cookie -- this also keeps Node-dependent
      // hooks (useUser) working, since they read from localStorage, not the cookie.
      if (data?.token) setAuthToken(data.token);

      return data;
    },
  });
};

export const useRegisterPassword = () => {
  return useMutation({
    mutationFn: async ({ password, token }: RegisterPasswordParams) => {
      const { data } = await edupassClient.post(
        '/registrar',
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data?.token) setAuthToken(data.token);

      return data;
    },
  });
};
